import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardBody } from "reactstrap";
import { useNotificationsApi } from "../../../Notifications/NotificationsPage/api";
import { useNotificationRedirect } from "../../../Notifications/useNotificationRedirect";
import InfiniteScroll from "react-infinite-scroll-component";
import List from "../../../Notifications/NotificationsPage/List";
import Loading from "../../../../components/Loaders/Loading";
import { useReadNotifications } from "../../../Notifications/useReadNotifications";
import { useMapNotifications } from "../../../Notifications/useMapNotifcations";
import "../../../Notifications/NotificationsPage/style.scss";

const ALL_TABS = ["all", "share-user", "scheduler", "leave", "timeclock", "timesheet", "people"];
const CHANNEL_KEYS = {
    all: "all",
    "share-user": "shareUser",
    scheduler: "scheduler",
    leave: "leave",
    timeclock: "timeclock",
    timesheet: "timesheet",
    people: "people",
};

const NotificationsWidget = ({ channels }) => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const { data, setData, totalItems, getAll, loading } = useNotificationsApi();
    const { readOne } = useReadNotifications();
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const tabs = useMemo(
        () => (channels
            ? ALL_TABS.filter((tab) => !!channels[CHANNEL_KEYS[tab]])
            : ALL_TABS),
        [channels],
    );

    const [activeTab, setActiveTab] = useState(tabs[0] || "all");

    const notifications = useMapNotifications(data, t);

    const toggleTab = useCallback(
        (tab) => {
            setActiveTab(tab);
            setPage(1);
            setData([]);
        },
        [setActiveTab, setData]
    );

    useEffect(() => {
        if (tabs.length && !tabs.includes(activeTab)) {
            setActiveTab(tabs[0]);
            setPage(1);
            setData([]);
        }
    }, [activeTab, setData, tabs]);

    const redirectNotification = useNotificationRedirect();

    const readNotifications = useCallback(
        async (id) => {
            await readOne(id);

            setData((prevData) => {
                return prevData?.map((item) => {
                    if (item.id === id) {
                        return { ...item, read: true };
                    }
                    return item;
                });
            });
        },
        [setData, readOne]
    );

    const navigateToDetails = useCallback(
        (notification) => {
            const { type, data } = notification;
            readNotifications(notification.id);
            redirectNotification(type, data);
        },
        [redirectNotification, readNotifications]
    );

    const next = useCallback(() => {
        if (totalItems > (notifications?.length || 0)) {
            setPage((prev) => prev + 1);
        }
    }, [setPage, notifications?.length, totalItems]);

	useEffect(() => {
		if (!ref.current) {
			return;
		}
		const messageHeight = 60;
		setPerPage(Math.ceil(ref.current.clientHeight / messageHeight));
	}, [setPerPage, data]);

    useEffect(() => {
        const controller = new AbortController();
        getAll({ read: false, page, perPage, channel: activeTab === "all" ? undefined : activeTab }, controller);
        return () => controller.abort();
    }, [getAll, page, perPage, activeTab]);

    return (
        <div className="bg-white h-100">
            <CardHeader className="border-0">
                <h3>{t("notification")}</h3>
                <div className="d-flex gap-4 mt-4 flex-wrap">
                    {tabs.map((tab) => {
                        return (
                            <span
                                key={tab}
                                className={`cursor-pointer ${
                                    activeTab === tab && "text-primary border-bottom border-primary"
                                }`}
                                onClick={() => toggleTab(tab)}
                            >
                                {t(tab)}
                            </span>
                        );
                    })}
                </div>
            </CardHeader>
            <div
                className="px-3"
                id="notificationPage"
                ref={ref}
                style={{ overflow: "auto", overflowX: "none", height: "calc(100% - 95px)" }} 
                scrollThreshold={0.5}
            >
                {data?.length === 0 && loading ? (
                    <div className="h-100 d-flex justify-content-center align-items-center">
                        <Loading />
                    </div>
                ) : data?.length ? (
                    <InfiniteScroll
                        dataLength={notifications?.length || 0}
                        next={next}
                        scrollableTarget="notificationPage"
                        hasMore={totalItems > (notifications?.length || 0)}
                        scrollThreshold={0.5}
                        style={{ overflow: "none" }}
                        loader={
                            <div className="d-flex align-items-center justify-content-center my-1">
                                <Loading />
                            </div>
                        }
                    >
                        <List data={notifications} readNotifications={readNotifications} navigateToDetails={navigateToDetails}/>
                    </InfiniteScroll>
                ) : (
                    <div className="h-100 d-flex flex-column gap-2 align-items-center pt-5">
                        <i className="fa-solid fa-inbox text-muted" style={{ fontSize: "20px" }} />
                        <h4 className="text-gray font-weight-400">{t("no-data")}</h4>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsWidget;
